'use strict';

/**
 * /ts/login.ts - the pre-login page. Three steps, username first: resolve
 * which single factor the account needs (passkey or password, never both),
 * then complete it. No toast-stack/dialog infra here (neither is part of
 * this page's own small bundle, see lib/Assets.php's LOGIN_JS_FILES) -- one
 * inline error element covers it.
 */

/** Matches lib/Passkey.php's startAuthentication() JSON shape, before the
 * base64url fields below are converted to real ArrayBuffers for the
 * credentials API. */
interface PasskeyRequestOptions {
	challenge: string;
	rpId: string;
	timeout: number;
	userVerification: string;
	allowCredentials: { type: string; id: string }[];
}

type LoginStep = 'username' | 'passkey' | 'password';

const PASSKEY_BUTTON_DEFAULT_LABEL = 'Use your passkey';
const PASSKEY_BUTTON_WAITING_LABEL = 'Waiting for your passkey';

/** Set once loginStart() resolves a username to a factor; the password
 * step's form only collects the password, not the username again. */
let pendingUsername = '';

function showStep(step: LoginStep): void {
	for (const el of document.querySelectorAll<HTMLElement>('[data-step]')) {
		el.hidden = el.dataset.step !== step;
	}
	// Passkey is the one step that doesn't wait for a second click -- the
	// username submit that got us here is itself the user gesture WebAuthn
	// needs, so usePasskey() fires immediately instead of focusing the
	// button for a manual click.
	if (step === 'passkey') void usePasskey();
	else if (step === 'password') requireEl<HTMLInputElement>('login-password').focus();
	else requireEl<HTMLInputElement>('login-username').focus();
}

function showError(err: unknown): void {
	const el = requireEl<HTMLElement>('login-error');
	el.textContent = err instanceof Error ? err.message : String(err);
	el.hidden = false;
}

function hideError(): void {
	requireEl<HTMLElement>('login-error').hidden = true;
}

function setAccountLabel(username: string): void {
	for (const p of document.querySelectorAll<HTMLElement>('.login-account')) {
		p.textContent = `Signing in as ${username}`;
	}
}

/** Post-login destination; the server already validates next=, this is the
 * belt-and-braces same-origin check at the point of navigation. */
function nextUrl(): string {
	try {
		const url = new URL(requireEl<HTMLInputElement>('login-next').value || '/', location.origin);
		return url.origin === location.origin ? url.href : '/';
	} catch {
		return '/';
	}
}

function resetToUsername(): void {
	pendingUsername = '';
	hideError();
	requireEl<HTMLFormElement>('login-password-form').reset();
	showStep('username');
}

for (const button of document.querySelectorAll<HTMLButtonElement>('[data-back]')) {
	button.addEventListener('click', resetToUsername);
}

requireEl<HTMLFormElement>('login-username-form').addEventListener('submit', async e => {
	e.preventDefault();
	hideError();
	const username = requireEl<HTMLInputElement>('login-username').value.trim();
	if (!username) return;

	try {
		const { factor } = await api<{ factor: 'passkey' | 'password' }>(
			'/api/auth/login/start',
			{ username },
			'POST'
		);
		pendingUsername = username;
		setAccountLabel(username);
		showStep(factor);
	} catch (err) {
		showError(err);
	}
});

/**
 * Runs the passkey ceremony -- auto-fired by showStep('passkey') and also
 * wired to the button's own click, so a manual retry after a failure (the
 * button is re-enabled below, in the catch) works the same way.
 */
async function usePasskey(): Promise<void> {
	hideError();
	const button = requireEl<HTMLButtonElement>('login-passkey-button');

	if (!passkeysSupported()) {
		showError(new Error(PASSKEY_UNSUPPORTED_MESSAGE));
		return;
	}

	button.disabled = true;
	button.textContent = PASSKEY_BUTTON_WAITING_LABEL;

	try {
		const { publicKey } = await api<{ publicKey: PasskeyRequestOptions }>(
			'/api/auth/login/passkey/start',
			{},
			'POST'
		);

		const credential = (await navigator.credentials.get({
			publicKey: {
				challenge: b64uToBuf(publicKey.challenge),
				rpId: publicKey.rpId,
				timeout: publicKey.timeout,
				userVerification: publicKey.userVerification as UserVerificationRequirement,
				allowCredentials: publicKey.allowCredentials.map(c => ({
					type: 'public-key' as const,
					id: b64uToBuf(c.id)
				}))
			}
		})) as PublicKeyCredential | null;
		if (!credential) throw new Error('No passkey was selected');

		const response = credential.response as AuthenticatorAssertionResponse;
		const payload = {
			id: credential.id,
			response: {
				clientDataJSON: bufToB64u(response.clientDataJSON),
				authenticatorData: bufToB64u(response.authenticatorData),
				signature: bufToB64u(response.signature)
			}
		};

		await api('/api/auth/login/passkey/finish', { credential: JSON.stringify(payload) }, 'POST');
		location.href = nextUrl();
	} catch (err) {
		showError(err);
		button.disabled = false;
		button.textContent = PASSKEY_BUTTON_DEFAULT_LABEL;
	}
}

requireEl<HTMLButtonElement>('login-passkey-button').addEventListener('click', () => void usePasskey());

requireEl<HTMLFormElement>('login-password-form').addEventListener('submit', async e => {
	e.preventDefault();
	hideError();
	const password = requireEl<HTMLInputElement>('login-password').value;

	try {
		await api('/api/auth/login/password', { username: pendingUsername, password }, 'POST');
		location.href = nextUrl();
	} catch (err) {
		showError(err);
	}
});
